import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import './SimpleAccordion.css'; // Sukursime CSS failą atskirai

/**
 * Paprastas Accordion komponentas, kuris veikia kaip nekontroliuojamas komponentas
 * ir neturi problemų dėl controlled/uncontrolled statuso pakeitimų.
 */
const SimpleAccordion = ({
    title,
    children,
    defaultExpanded = false,
    expanded, // Add controlled 'expanded' prop
    onToggle,
    className = '',
    headerClassName = '',
    contentClassName = '',
    expandIconClassName = '',
    id
}) => {
    const { t } = useTranslation();
    // Determine if the component is controlled
    const isControlled = expanded !== undefined;
    // Internal state only used if not controlled
    const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);

    // Use controlled state if provided, otherwise use internal state
    const currentExpanded = isControlled ? expanded : internalExpanded;

    // Updated toggle function
    const toggleAccordion = (e) => {
        e.stopPropagation();
        if (isControlled) {
            // If controlled, just call the callback provided by the parent
            if (onToggle) {
                // Pass the current state to allow parent to decide the new state
                onToggle(!currentExpanded);
            }
        } else {
            // If uncontrolled, toggle internal state
            const newExpandedState = !internalExpanded;
            setInternalExpanded(newExpandedState);
            if (onToggle) {
                // Also call callback if provided for uncontrolled case
                onToggle(newExpandedState);
            }
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleAccordion(e);
        }
    };

    // Sukuriame unikalų id jei nepateiktas
    const accordionId = id || `accordion-${Math.random().toString(36).substr(2, 9)}`;
    const contentId = `${accordionId}-content`;

    return (
        <div className={`simple-accordion ${className} ${currentExpanded ? 'expanded' : ''}`}>
            <div 
                className={`simple-accordion-header ${headerClassName}`}
                onClick={toggleAccordion}
                onKeyDown={handleKeyDown}
                role="button"
                tabIndex={0}
                aria-expanded={currentExpanded}
                aria-controls={contentId}
                id={accordionId}
            >
                <div className="simple-accordion-title">
                    {typeof title === 'string' ? t(title) : title}
                </div>
                
                <div className={`simple-accordion-icon ${expandIconClassName} ${currentExpanded ? 'expanded' : ''}`}>
                    <ExpandMoreIcon />
                </div>
            </div>
            
            {/* Show content based on the current expansion state */}
            {currentExpanded && (
                <div 
                    className={`simple-accordion-content ${contentClassName}`}
                    id={contentId}
                    aria-labelledby={accordionId}
                    role="region"
                >
                    {children}
                </div>
            )}
        </div>
    );
};

SimpleAccordion.propTypes = {
    /** Antraštės turinys - gali būti tekstas arba komponentas */
    title: PropTypes.node.isRequired,
    /** Accordion turinio komponentai */
    children: PropTypes.node.isRequired,
    /** Ar accordion pradžioje išskleistas (naudojama tik nekontroliuojamam režimui) */
    defaultExpanded: PropTypes.bool,
    /** Kontroliuojama išskleidimo būsena (jei pateikta, komponentas tampa kontroliuojamas) */
    expanded: PropTypes.bool,
    /** Funkcija iškviečiama paspaudus antraštę. Perduoda siūlomą naują būseną (true/false) */
    onToggle: PropTypes.func,
    /** Pagrindinio elemento CSS klasė */
    className: PropTypes.string,
    /** Antraštės elemento CSS klasė */
    headerClassName: PropTypes.string,
    /** Turinio elemento CSS klasė */
    contentClassName: PropTypes.string,
    /** Išskleidimo ikonos CSS klasė */
    expandIconClassName: PropTypes.string,
    /** Unikalus elemento id */
    id: PropTypes.string
};

export default SimpleAccordion;
